// Pure (React-free) metadata for the AI Assistant connect dialog, so the
// "which platform is live, and which instruction set does it use" decisions
// are unit-testable without rendering the dialog.

export type LlmPlatformId = 'claude' | 'chatgpt' | 'gemini' | 'grok';

/** Which i18n step set a platform's connect flow uses. Claude keeps the original
 *  `aiAssistant.step*` keys; every other platform has its own block under `aiAssistant.<flow>.*`
 *  (ChatGPT: Plugins > Add custom MCP server; Gemini: Settings > Connected Apps; Grok: Connectors). */
export type ConnectFlow = 'claude' | 'chatgpt' | 'gemini' | 'grok';

export interface LlmPlatformMeta {
    id: LlmPlatformId;
    name: string;
    connectUrl: string;
    /** Staged, not yet connectable: rendered disabled with a "Coming Soon" pill. */
    comingSoon?: boolean;
    popular?: boolean;
    /** Required for a connectable platform; ignored for comingSoon ones. */
    flow?: ConnectFlow;
}

// All four platforms are connectable (Gemini + Grok verified compatible with our remote MCP + OAuth DCR, #531).
export const LLM_PLATFORM_META: readonly LlmPlatformMeta[] = [
    {
        id: 'claude',
        name: 'Claude',
        connectUrl: 'https://claude.ai/customize/connectors',
        popular: true,
        flow: 'claude',
    },
    {
        id: 'chatgpt',
        name: 'ChatGPT',
        connectUrl: 'https://chatgpt.com/',
        flow: 'chatgpt',
    },
    {
        id: 'gemini',
        name: 'Gemini',
        connectUrl: 'https://gemini.google.com/app',
        flow: 'gemini',
    },
    {
        id: 'grok',
        name: 'Grok',
        connectUrl: 'https://grok.com/',
        flow: 'grok',
    },
];

export const isPlatformConnectable = (p: Pick<LlmPlatformMeta, 'comingSoon'>): boolean => !p.comingSoon;

/** Translation-key prefixes for the platform-specific steps (2 = open settings, 3 = create connector). */
export const flowKeyPrefix = (flow: ConnectFlow | undefined): { step2: string; step3: string; notes: string | null } =>
    flow === 'chatgpt' || flow === 'gemini' || flow === 'grok'
        ? { step2: `aiAssistant.${flow}.step2`, step3: `aiAssistant.${flow}.step3`, notes: `aiAssistant.${flow}.notes` }
        : { step2: 'aiAssistant.step2', step3: 'aiAssistant.step3', notes: null };

/** Number of numbered instructions under step 3 (both flows have four). */
export const STEP3_INSTRUCTION_COUNT = 4;

/** ChatGPT notes shown under the steps: paid plan, menu names, Blueticks free limit. */
export const CHATGPT_NOTE_KEYS = ['paidPlan', 'menuNames', 'freeLimit'] as const;

/** Gemini notes: who can add a custom app (US, 18+, personal account, English, Keep Activity on) and the write confirmation. */
export const GEMINI_NOTE_KEYS = ['eligibility', 'activity', 'confirm'] as const;

/** Grok notes: menu names vary; leave the OAuth client fields empty. */
export const GROK_NOTE_KEYS = ['menuNames', 'clientFields'] as const;

/** Note keys (under `<notes>.`) shown for a flow; empty for Claude, which has no notes block. */
export const noteKeysFor = (flow: ConnectFlow | undefined): readonly string[] =>
    flow === 'chatgpt' ? CHATGPT_NOTE_KEYS : flow === 'gemini' ? GEMINI_NOTE_KEYS : flow === 'grok' ? GROK_NOTE_KEYS : [];

/**
 * #137 B2 — inline "Quick path" under the Copy-URL control (the measured stall is platform -> copy
 * URL: 85% of converters take that step vs 61% of non-converters). Build-time switch, ships OFF.
 * Same pattern as CALENDAR_STEP_SKIP_ENABLED / PAIRING_CODE_ENABLED.
 */
export const MCP_INLINE_STEPS_ENABLED = false;

/** i18n keys for the three inline steps; step 2 differs per flow, steps 1 and 3 are shared. */
export const inlineStepKeys = (flow: ConnectFlow | undefined): [string, string, string] => [
    'aiAssistant.inline.copy',
    flow === 'chatgpt' || flow === 'gemini' || flow === 'grok' ? `aiAssistant.inline.${flow}` : 'aiAssistant.inline.claude',
    'aiAssistant.inline.paste',
];

/**
 * #493 C — one-click "Add to {platform}": one button copies the MCP URL and opens the platform's
 * add-connector screen. Neither platform accepts a prefilled server URL, so copy + open is the
 * shortest path. Targets checked live in a logged-in browser on 2026-10-24: Claude's bare
 * `?modal=add-custom-connector` opens the modal (extra params make it redirect away); ChatGPT's
 * add-server modal has no URL, so we land on /plugins.
 */
export interface OneClickTarget {
    openUrl: string;
    hintKey: string;
}

const ONE_CLICK_TARGETS: Partial<Record<ConnectFlow, OneClickTarget>> = {
    claude: {
        openUrl: 'https://claude.ai/customize/connectors?modal=add-custom-connector',
        hintKey: 'aiAssistant.oneClick.claudeHint',
    },
    chatgpt: {
        openUrl: 'https://chatgpt.com/plugins',
        hintKey: 'aiAssistant.oneClick.chatgptHint',
    },
    // No verified deep link into Gemini's Connected Apps screen (Google's help page lists the menu path
    // only), so open the app and let the hint name the steps.
    gemini: {
        openUrl: 'https://gemini.google.com/app',
        hintKey: 'aiAssistant.oneClick.geminiHint',
    },
};

export const oneClickTarget = (flow: ConnectFlow | undefined): OneClickTarget | null =>
    flow ? ONE_CLICK_TARGETS[flow] ?? null : null;

/** i18n keys under `aiAssistant.oneClick` (every locale must carry all of them). */
export const ONE_CLICK_COPY_KEYS = ['button', 'subtitle', 'copied', 'claudeHint', 'chatgptHint', 'geminiHint', 'manual'] as const;

/**
 * Runtime host gate: ON for app-staging, prod and local dev. Prod was enabled (#493) after the
 * staging validation of the Claude and ChatGPT buttons.
 */
const ONE_CLICK_ADD_HOSTS: ReadonlySet<string> = new Set(['app-staging.blueticks.co', 'app.blueticks.co', 'localhost', '127.0.0.1']);

export const isOneClickAddEnabled = (hostname: string): boolean => ONE_CLICK_ADD_HOSTS.has(hostname);

/** Props for the reused mcp_url_copied / mcp_go_to_platform_clicked events (no backend change). */
export const oneClickEventProps = (platform: LlmPlatformId): { platform: LlmPlatformId; via: 'one_click' } => ({
    platform,
    via: 'one_click',
});
